/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack, Typography } from '@mui/material';
import { RootState } from 'packages/blu-booking/src/store';
import { Scheduler } from './UI/Scheduler';
import { memo, useMemo } from 'react';
import { useSchedulerData } from './hooks/useSchedulerData';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { CalendarLoader } from '../../UI/CalendarLoader/CalendarLoader';
import { selectSoloOrario } from '../../hooks/useSoloOrario';
import { useFinestraOraria } from '../../hooks/useFinestraOraria';
import {
  getAppuntamentiDelleColonne,
  selectAppuntamentiUtentePronti,
} from '../../utility_objects/AppuntamentiGiornalieri';
import {
  FINESTRA_PREDEFINITA_UTENTE,
  OrariUtentiServer,
  getOrariUtenti,
  getUtentiInColonna,
} from './utils/OrariUtente';

/**
 * Wrapper del calendario User.
 * Usa useSchedulerData per il data fetching e gestisce
 * il rendering condizionale (nessun utente per il PdV).
 */
export const UserCalendarInner = memo(
  function UserCalendarInner(): JSX.Element {
    const { selectedDate, selectedPointOfSale, view } = useSelector(
      (state: RootState) => state.bluBookingUserCalendar
    );
    // Riga Ore del riquadro Visualizzazione di questo calendario
    const soloOrario = useSelector(selectSoloOrario('utente'));
    const appointments = useSelector(
      (state: RootState) => state.bluBookingaAppointments.appointments
    );
    const appuntamentiPronti = useSelector(selectAppuntamentiUtentePronti);

    const {
      calendarPointOfSale,
      shiftPointOfSale,
      isLoading,
      isSuccessCalendarPointOfSale,
    } = useSchedulerData({
      selectedDate,
      selectedPointOfSale,
    });

    // La banda che il giornaliero disegna: le fasce di tutti gli utenti in
    // colonna, nel giorno mostrato, dagli stessi orari che riceve lo Scheduler
    // (la query non dà un tipo ai dati: sono quelli di normalize)
    const giorno = dayjs(selectedDate).format('YYYY-MM-DD');
    const orariUtenti = calendarPointOfSale as OrariUtentiServer | undefined;
    const orari = useMemo(
      () => getOrariUtenti(orariUtenti, giorno),
      [orariUtenti, giorno]
    );
    // Solo gli appuntamenti degli utenti in colonna: Syncfusion non disegna
    // gli altri, che non devono allargare la finestra
    const appuntamenti = useMemo(
      () =>
        getAppuntamentiDelleColonne(
          appointments,
          'userId',
          getUtentiInColonna(orariUtenti)
        ),
      [appointments, orariUtenti]
    );

    // Qui e non nello Scheduler, che si rimonta a ogni giorno, punto vendita o
    // caricamento degli appuntamenti: la finestra tenuta passa da un montaggio
    // all'altro, senza tornare alle ore di sempre, che qui partono dalle 7.
    // Verticale e Orizzontale mostrano lo stesso giorno, con la stessa finestra
    const finestra = useFinestraOraria({
      soloOrario,
      conPulsante: !!selectedPointOfSale,
      view,
      selectedDate,
      orari,
      appuntamenti,
      appuntamentiPronti,
      sfondoPronto: isSuccessCalendarPointOfSale,
      predefinita: FINESTRA_PREDEFINITA_UTENTE,
    });

    const GetContent = useMemo(() => {
      // Ore «Prenotabili» senza una finestra da mostrare (prima apertura,
      // dati non pronti): il calendario non si monta, resta il loader
      if (isLoading || finestra.inAttesa) {
        return (
          <Stack
            alignItems="center"
            justifyContent="center"
            direction="row"
            flex="1"
            sx={{
              height: '100%',
              width: '100%',
              backgroundColor: 'var(--base-foreground-color) ',
            }}
          />
        );
      }
      // Mostra messaggio se non ci sono utenti per il PdV selezionato
      if (
        !isLoading &&
        calendarPointOfSale &&
        shiftPointOfSale &&
        ((calendarPointOfSale as any).length === 0 ||
          (shiftPointOfSale as any).length === 0)
      ) {
        return (
          <div style={{ height: '100%' }}>
            <Stack
              alignItems="center"
              justifyContent="center"
              direction="row"
              flex="1"
              sx={{ height: '100%', width: '100%' }}
            >
              <Typography fontWeight="bold">
                Nessun utente per il punto vendita selezionato
              </Typography>
            </Stack>
          </div>
        );
      }
      return (
        <Scheduler
          calendarPointOfSale={calendarPointOfSale}
          shiftPointOfSale={shiftPointOfSale}
          startHour={finestra.startHour}
          endHour={finestra.endHour}
        />
      );
    }, [
      calendarPointOfSale,
      isLoading,
      shiftPointOfSale,
      finestra.startHour,
      finestra.endHour,
      finestra.inAttesa,
    ]);

    return (
      <div style={{ height: '100%' }}>
        {GetContent}
        {/* Ore «Prenotabili» e dati non ancora pronti: il loader resta
            aperto anche sopra il calendario montato */}
        <CalendarLoader open={isLoading || finestra.provvisoria} />
      </div>
    );
  }
);
